import { type ItineraryDTO } from "../../../../../types/dtos/api/itinerary/itinerary.dto";
import { type RentalLegPropertiesDTO } from "../../../../../types/dtos/api/rental/rental-leg-properties.dto";
import { isNonEmptyText, resolveBookingUrlFromBookingUrls } from "./OfferPricingUrl.helper";

/**
 * Resolves a provider homepage or deep-link URL from rental leg metadata.
 *
 * @param rental - Rental properties attached to an itinerary leg.
 * @returns Provider URL when `provider.url` or `uris` are set, otherwise `null`.
 */
export const resolveRentalLegProviderUrl = ( rental: RentalLegPropertiesDTO | undefined ): string | null => {
    const providerUrl = rental?.provider?.url;
    if ( isNonEmptyText( providerUrl ) ) {
        return providerUrl.trim();
    }

    const uris = rental?.uris;
    if ( uris !== undefined ) {
        return resolveBookingUrlFromBookingUrls( uris );
    }

    return null;
};

/**
 * Resolves the provider URL for a pricing segment from its itinerary legs.
 *
 * @param itinerary - Loaded itinerary including `pricingSegments` and `legs`.
 * @param segmentId - Canonical pricing segment identifier.
 * @returns First non-empty provider URL found on segment legs, or `null`.
 */
export const resolvePricingSegmentProviderUrl = (
    itinerary: ItineraryDTO,
    segmentId: string,
): string | null => {
    const pricingSegment = itinerary.pricingSegments?.find( ( segment ) => segment.id === segmentId );
    if ( pricingSegment === undefined ) {
        return null;
    }

    for ( const legId of pricingSegment.legIds ) {
        const leg = itinerary.legs.find( ( candidate ) => candidate.id === legId );
        const providerUrl = resolveRentalLegProviderUrl( leg?.rental );
        if ( providerUrl !== null ) {
            return providerUrl;
        }
    }

    return null;
};
